// ---------------------------------------------------------------------------
// Room code entry: four mono character boxes.
//
// One real input sits transparently over the boxes rather than one input per
// box. Per-box inputs fight the software keyboard, break paste, and lose a
// character whenever autofill fires — and a player reading a code off a friend's
// screen pastes it as often as types it.
//
// The focus state is the point of this component, not decoration on it. The
// input itself is invisible, so nothing about the native caret, the native
// highlight or the platform focus ring reaches the player: with the boxes
// styled the same focused and not, there was no way to tell whether typing
// would go here. So the two states are made to look like different controls:
//
//   idle     dashed grey outlines on the card's own surface, saying nothing
//   focused  a blue panel behind the whole group, solid outlines, and a caret
//            blinking in the box the next character lands in
//
// Nothing about the change moves anything. The wash, the ring and the caret are
// all painted inside space that is reserved whether or not they are there, and
// the border stays 1px in every state, so the boxes never resize under the
// player's thumb.
//
// **What gets pasted here is usually a link, not a code.** Somebody sharing a
// room sends `https://countdown.iyk.gg/room/A2FE`, and the person on the other
// end pastes the whole thing. Stripping the punctuation out of that and keeping
// the first four characters spells `HTTP`, which is a code the server will
// refuse for reasons the player cannot see. `roomCodeFrom` takes the code out
// of the link instead, on every path into the field, so it costs nothing to
// paste the message rather than the code out of it.
//
// That is also why there is no `maxLength` on the input: a browser truncates a
// paste to it *before* anything can look at what was pasted, so the whole link
// would have been four characters gone by the time it reached this file. The
// length is enforced by the slice in `roomCodeFrom`, and React restores the
// element's value from the prop when a change is ignored, so an over-long entry
// still cannot survive in the box.
// ---------------------------------------------------------------------------

import { useState, type ReactElement } from 'react';

/** Anything that looks like this game's room URL, however it is wrapped. */
const ROOM_LINK = /\/room\/([a-z0-9]+)/i;

/**
 * The room code inside whatever was typed, pasted or dropped in.
 *
 * Exported because it is the only part of this component with an answer that
 * can be wrong, and the link case is the one worth pinning: `roomCodeFrom` is
 * the difference between pasting a message from a friend and retyping the four
 * characters out of it.
 *
 * A link wins over the rest of the string rather than being cleaned alongside
 * it. Everything else — case, spaces, the dashes somebody types into a code —
 * is stripped, and the result is cut to `length`.
 */
export function roomCodeFrom(raw: string, length = 4): string {
  const link = ROOM_LINK.exec(raw);
  const source = link === null ? raw : link[1];
  return source
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, length);
}

export function CodeInput(props: {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  label?: string;
  autoFocus?: boolean;
  onComplete?: (value: string) => void;
  /** Greyed and inert, for a code that is being checked against the server. */
  disabled?: boolean;
  /** The last attempt was refused. Colours the boxes without moving them. */
  invalid?: boolean;
  /** Id of the element carrying the hint or the error underneath. */
  describedBy?: string;
}): ReactElement {
  const {
    value,
    onChange,
    length = 4,
    label = 'room code',
    autoFocus = false,
    onComplete,
    disabled = false,
    invalid = false,
    describedBy,
  } = props;

  const [focused, setFocused] = useState(false);

  const handle = (raw: string) => {
    const cleaned = roomCodeFrom(raw, length);
    if (cleaned === value) return;
    onChange(cleaned);
    if (cleaned.length === length) onComplete?.(cleaned);
  };

  // Where the next character lands. Clamped, so a full code leaves the caret on
  // the last box rather than off the end of the row.
  const caret = Math.min(value.length, length - 1);
  const live = focused && !disabled;

  return (
    <div
      className={[
        'group relative flex w-fit items-center gap-2 rounded-[14px] p-2',
        'transition-[background-color,box-shadow] duration-150 ease-out',
        live
          ? invalid
            ? 'bg-alert/15 ring-2 ring-alert'
            : 'bg-blue-700/30 ring-2 ring-blue-400'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {Array.from({ length }, (_, index) => {
        const character = value[index] ?? '';
        const active = live && index === caret;

        const box = disabled
          ? 'border-solid border-line bg-raised text-ink-dim'
          : invalid
            ? 'border-solid border-alert bg-surface text-ink'
            : character
              ? 'border-solid border-blue-500 bg-surface text-ink'
              : active
                ? 'border-solid border-blue-400 bg-surface text-ink'
                : live
                  ? 'border-solid border-line-strong bg-surface-2 text-ink'
                  : 'border-dashed border-line bg-surface-2 text-ink group-hover:border-line-strong';

        return (
          <div
            key={index}
            aria-hidden="true"
            className={`grid h-[42px] w-[34px] place-items-center rounded-lg border font-mono text-[18px] font-bold transition-colors duration-150 ease-out ${box}`}
          >
            {character || (active && value.length < length ? <Caret /> : '')}
          </div>
        );
      })}

      <input
        value={value}
        onChange={(event) => handle(event.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        aria-label={label}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        inputMode="text"
        // No `maxLength`: see the note at the top. It would eat a pasted link
        // four characters in, before `roomCodeFrom` could find the code in it.
        // The native focus ring would sit on an invisible element, so it is
        // dropped in favour of the treatment above, which is drawn on the boxes
        // a player is actually looking at.
        className="absolute inset-0 h-full w-full cursor-text opacity-0 outline-none disabled:cursor-not-allowed"
      />
    </div>
  );
}

/** A text caret, since the real one is inside an invisible input. */
function Caret(): ReactElement {
  return <span aria-hidden="true" className="h-[22px] w-[2px] animate-pulse rounded-full bg-blue-200" />;
}
